/**
 * Roamify destinations — same snapshot as the AlbaVox mobile app
 * (`roamifyCountries.json` from GET /api/ESim/packages catalog).
 */
import roamifyCountriesJson from "./esim/roamifyCountries.json";

export type RoamifyGeography = "local" | "regional" | "global";

export type RoamifyCountryEntry = {
  code: string;
  name: string;
  region: string;
  geography: RoamifyGeography;
  slug: string;
  imageUrl: string;
};

export type EsimDestination = {
  code: string;
  name: string;
  region: string;
  geography: RoamifyGeography;
  /** flagcdn / Roamify image URL */
  flag: string;
};

type RoamifyCountriesFile = {
  source: string;
  generatedAt: string;
  counts: {
    local: number;
    regional: number;
    global: number;
    total: number;
  };
  countries: RoamifyCountryEntry[];
};

const data = roamifyCountriesJson as RoamifyCountriesFile;

const byName = (a: RoamifyCountryEntry, b: RoamifyCountryEntry) =>
  a.name.localeCompare(b.name);

/** Closest ISO-2 for flagcdn when Roamify uses a non-standard / premium code. */
const FLAG_CODE_ALIASES: Record<string, string> = {
  NOC: "cy",
  "GB-SCT": "gb",
  USS: "us",
  TNP: "tn",
  CNM: "cn",
  NZP: "nz",
  "BQ-SE": "bq",
  "PT-20": "pt",
  JPL: "jp",
  "PT-30": "pt",
  USL: "us",
  RUP: "ru",
  "PT-40": "gp",
  OMP: "om",
  EGP: "eg",
  "BQ-SA": "bq",
  XK: "xk",
  IC: "ic",
  // Dissolved Netherlands Antilles — flagcdn has no `an`; use Curaçao flag only.
  AN: "cw",
};

/**
 * ISO-2 used for Intl.DisplayNames (uppercase).
 * Must NOT reuse flag aliases (e.g. AN→cw would label Antilles as Curaçao).
 */
export function iso2ForRoamifyCode(code: string): string | null {
  if (/^[A-Za-z]{2}$/.test(code)) return code.toUpperCase();
  return null;
}

const displayNamesCache = new Map<string, Intl.DisplayNames>();

function getCountryDisplayNames(locale: string): Intl.DisplayNames | null {
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return null;
  }
  const cached = displayNamesCache.get(locale);
  if (cached) return cached;
  try {
    const dn = new Intl.DisplayNames([locale, "en"], { type: "region" });
    displayNamesCache.set(locale, dn);
    return dn;
  } catch {
    return null;
  }
}

/**
 * Localize a country/region label from ISO code + catalog fallback name.
 */
export function localizedCountryLabel(
  code: string | null | undefined,
  fallbackName: string | null | undefined,
  options: {
    locale: string;
    t: (key: string) => string;
    geography?: RoamifyGeography | string | null;
  },
): string {
  const safeCode = (code ?? "").trim();
  const safeName = (fallbackName ?? "").trim();
  const geography = String(options.geography ?? "local")
    .trim()
    .toLowerCase() as RoamifyGeography | string;

  if (safeCode || safeName) {
    return localizedDestinationName(
      {
        code: safeCode || safeName,
        name: safeName || safeCode,
        geography:
          geography === "regional" || geography === "global"
            ? geography
            : "local",
      },
      { locale: options.locale, t: options.t },
    );
  }
  return safeName || safeCode;
}

export function localizedGeographyLabel(
  geography: string | null | undefined,
  t: (key: string) => string,
): string {
  const geo = String(geography ?? "")
    .trim()
    .toLowerCase();
  if (geo === "local") return t("esim.geography_local");
  if (geo === "regional") return t("esim.geography_regional");
  if (geo === "global") return t("esim.geography_global");
  return (geography ?? "").trim();
}

export function localizedRegionLabel(
  region: string | null | undefined,
  t: (key: string) => string,
): string {
  const value = (region ?? "").trim();
  if (!value) return "";
  const key = `esim.regions.${value}`;
  const translated = t(key);
  // Only keep known regional package codes; skip raw English continent names.
  if (translated && translated !== key) return translated;
  return "";
}

/**
 * Localize a Roamify destination label for the active UI locale.
 * Local countries use Intl.DisplayNames; regional/global use i18n keys.
 */
export function localizedDestinationName(
  destination: Pick<EsimDestination, "code" | "name" | "geography">,
  options: {
    locale: string;
    t: (key: string) => string;
  },
): string {
  if (destination.geography !== "local") {
    const key = `esim.regions.${destination.code}`;
    const translated = options.t(key);
    if (translated && translated !== key) return translated;
    return destination.name;
  }

  const iso = iso2ForRoamifyCode(destination.code);
  // Codes that must keep Roamify's catalog name (DisplayNames remaps them wrongly).
  const preferCatalogName = new Set(["AN"]);
  if (iso && !preferCatalogName.has(iso)) {
    const dn = getCountryDisplayNames(options.locale);
    const localized = dn?.of(iso);
    // DisplayNames returns the code itself when unknown.
    if (localized && localized.toUpperCase() !== iso) {
      return localized;
    }
  }

  return destination.name;
}

export type FlagCdnWidth = 40 | 80 | 160 | 320;

/**
 * Prefer flagcdn for ISO-2 (and known aliases). Fall back to Roamify imageUrl.
 */
export function flagForRoamifyCode(
  code: string,
  imageUrl?: string,
  size: FlagCdnWidth = 80,
): string {
  const alias = FLAG_CODE_ALIASES[code];
  const iso =
    alias || (/^[A-Za-z]{2}$/.test(code) ? code.toLowerCase() : null);

  if (iso) {
    return `https://flagcdn.com/w${size}/${iso}.png`;
  }
  if (imageUrl) return imageUrl;
  return `https://flagcdn.com/w${size}/un.png`;
}

function toDestination(entry: RoamifyCountryEntry): EsimDestination {
  return {
    code: entry.code,
    name: entry.name,
    region: entry.region || "Worldwide",
    geography: entry.geography,
    flag: flagForRoamifyCode(entry.code, entry.imageUrl, 80),
  };
}

/** Pinned at top of Country tab (same as mobile). */
export const PRIORITY_COUNTRY_CODES = ["AL", "ME", "MK", "SA", "XK"] as const;

export const LOCAL_COUNTRIES: EsimDestination[] = data.countries
  .filter((c) => c.geography === "local")
  // AN = dissolved Netherlands Antilles; Intl/CLDR often labels it "Curaçao",
  // which duplicates the real CW entry. Keep CW only.
  .filter((c) => c.code.toUpperCase() !== "AN")
  .slice()
  .sort(byName)
  .map(toDestination);

export const REGIONAL_PACKAGES: EsimDestination[] = data.countries
  .filter((c) => c.geography === "regional")
  .slice()
  .sort(byName)
  .map(toDestination);

export const GLOBAL_PACKAGES: EsimDestination[] = data.countries
  .filter((c) => c.geography === "global")
  .slice()
  .sort(byName)
  .map(toDestination);

/** Match `esim-{slug}-…` package ids back to a catalog destination. */
const PACKAGE_SLUGS = data.countries
  .map((c) => ({
    slug: c.slug.toLowerCase(),
    code: c.code,
    geography: c.geography,
  }))
  .filter((c) => c.slug.length > 0)
  .sort((a, b) => b.slug.length - a.slug.length);

export function resolveDestinationScopeFromPackageId(packageId: string): {
  countryCode?: string;
  regionalPackage?: string;
  geography?: RoamifyGeography;
} | null {
  const id = packageId.trim().toLowerCase();
  if (!id) return null;

  const withoutPrefix = id.startsWith("esim-") ? id.slice("esim-".length) : id;
  const match = PACKAGE_SLUGS.find(
    (c) =>
      withoutPrefix === c.slug ||
      withoutPrefix.startsWith(`${c.slug}-`) ||
      withoutPrefix.startsWith(`${c.slug}_`),
  );
  if (!match) return null;

  if (match.geography === "regional") {
    return { regionalPackage: match.code, geography: "regional" };
  }
  if (match.geography === "global") {
    return { countryCode: match.code, geography: "global" };
  }
  return { countryCode: match.code, geography: "local" };
}

export function sortDestinationsWithPriority(
  list: EsimDestination[],
  searchQuery: string,
): EsimDestination[] {
  const q = searchQuery.trim().toLowerCase();
  if (q) {
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.region.toLowerCase().includes(q),
    );
  }

  const priority = PRIORITY_COUNTRY_CODES.map((code) =>
    list.find((c) => c.code.toUpperCase() === code),
  ).filter(Boolean) as EsimDestination[];
  const prioritySet = new Set(
    PRIORITY_COUNTRY_CODES.map((c) => c.toUpperCase()),
  );
  const rest = list.filter((c) => !prioritySet.has(c.code.toUpperCase()));
  const seen = new Set<string>();
  return [...priority, ...rest].filter((c) => {
    if (seen.has(c.code)) return false;
    seen.add(c.code);
    return true;
  });
}

export function formatEsimPrice(price: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(price);
}

export function formatDataLabel(pkg: {
  isUnlimited: boolean;
  dataAmount: number;
}): string {
  if (pkg.isUnlimited) return "Unlimited";
  const gb = pkg.dataAmount / 1024;
  if (gb >= 1) return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  return `${pkg.dataAmount} MB`;
}

export const DESTINATIONS_PAGE_SIZE = 24;

export const destinationCardClass =
  "esim-dest-card group relative overflow-hidden rounded-2xl border border-border-soft bg-bg-base p-5 text-left transition duration-300 dark:bg-bg-subtle";

export const cardClass =
  "rounded-2xl border border-border-soft bg-bg-base p-5 text-left shadow-sm transition hover:border-brand-500/40 hover:shadow-md dark:bg-bg-subtle";

export const panelClass =
  "rounded-2xl border border-border-soft bg-bg-base p-4 shadow-sm dark:bg-bg-subtle";

export const inputClass =
  "w-full rounded-xl border border-border-soft bg-surface px-3 py-2 text-sm text-text-heading placeholder:text-text-subtle focus:outline-none focus:ring-2 focus:ring-brand-500";

export const errorBoxClass =
  "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300";
